import { useCallback, useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  getPricingDefaults,
  ServiceProviderPricingState,
} from "../../settings/serviceProviderPricingSlice";
import { ServiceProviderSettings } from "../../settings/api/serviceProviderPricingApi";
import { OfferFormData } from "../models/models";

/**
 * Hook to load the provider's pricing defaults from Settings > Pricing
 * and apply them to the offer creation form when creating a new offer.
 *
 * For existing draft offers (edit mode), defaults are NOT applied --
 * the saved offer values take precedence.
 */
const useOfferPricingDefaults = (
  formMethods: UseFormReturn<OfferFormData>,
  isNew: boolean
) => {
  const dispatch = useAppDispatch();
  const { settings, loading: pricingLoading } =
    useAppSelector<ServiceProviderPricingState>(
      (state: any) => state.serviceProviderPricing
    );

  const [fieldsFromDefaults, setFieldsFromDefaults] = useState<Set<string>>(
    new Set()
  );
  const defaultsApplied = useRef(false);

  // Fetch pricing defaults on mount
  useEffect(() => {
    dispatch(getPricingDefaults());
  }, [dispatch]);

  // Apply defaults to form only for new offers, only once
  useEffect(() => {
    if (!isNew || defaultsApplied.current || !settings) return;

    const appliedFields = new Set<string>();

    const setIfPresent = (
      field: keyof OfferFormData,
      value: number | string | boolean | null | undefined
    ) => {
      if (value !== undefined && value !== null) {
        formMethods.setValue(field, value as any, { shouldDirty: true });
        appliedFields.add(field);
      }
    };

    // Pricing rates
    setIfPresent("hourlyRate", settings.hourlyRate);
    setIfPresent("travelRatePerKm", settings.travelRatePerKm);
    setIfPresent("travelRatePerHour", settings.travelRatePerHour);

    // Terms
    setIfPresent("warrantyPeriodCode", settings.warrantyPeriodCode);
    setIfPresent("customWarrantyPeriod", settings.customWarrantyPeriod);
    setIfPresent("paymentTermsCode", settings.paymentTermsCode);
    setIfPresent("customPaymentTerms", settings.customPaymentTerms);

    // Validity
    setIfPresent("validityPeriodCode", settings.validityPeriodCode);
    setIfPresent("customValidityPeriod", settings.customValidityPeriod);

    // Included services (booleans)
    setIfPresent("includeDiagnostics", settings.includeDiagnostics);
    setIfPresent("includeLaborForRepair", settings.includeLaborForRepair);
    setIfPresent("includePartsAndMaterials", settings.includePartsAndMaterials);
    setIfPresent("includeTestingAndCalibration", settings.includeTestingAndCalibration);
    setIfPresent("includeWorkAreaCleanup", settings.includeWorkAreaCleanup);
    setIfPresent("includeDetailedServiceReport", settings.includeDetailedServiceReport);

    setFieldsFromDefaults(appliedFields);
    defaultsApplied.current = true;
  }, [isNew, settings, formMethods]);

  /**
   * Call this when a user modifies a pre-filled field
   * to remove the "from defaults" indicator for that field.
   */
  const clearDefaultIndicator = useCallback(
    (fieldName: string) => {
      setFieldsFromDefaults((prev) => {
        if (!prev.has(fieldName)) return prev;
        const next = new Set(prev);
        next.delete(fieldName);
        return next;
      });
    },
    []
  );

  const hasDefaults = !!(
    settings &&
    (settings.hourlyRate ||
      settings.travelRatePerKm ||
      settings.travelRatePerHour ||
      settings.warrantyPeriodCode ||
      settings.paymentTermsCode ||
      settings.validityPeriodCode ||
      settings.includeDiagnostics ||
      settings.includeLaborForRepair ||
      settings.includePartsAndMaterials ||
      settings.includeTestingAndCalibration ||
      settings.includeWorkAreaCleanup ||
      settings.includeDetailedServiceReport)
  );

  return {
    settings,
    pricingLoading,
    fieldsFromDefaults,
    clearDefaultIndicator,
    hasDefaults,
    defaultsApplied: defaultsApplied.current,
  };
};

export default useOfferPricingDefaults;
